import React from "react";
import {
  Box,
  Button,
  CircularProgress,
  Divider,
  IconButton,
  LinearProgress,
  Paper,
  Typography,
  Alert,
} from "@mui/material";
import CloudUploadRoundedIcon from "@mui/icons-material/CloudUploadRounded";
import AutoAwesomeRoundedIcon from "@mui/icons-material/AutoAwesomeRounded";
import EditRounded from "@mui/icons-material/EditRounded";
import DeleteRoundedIcon from "@mui/icons-material/DeleteRounded";
import DescriptionRoundedIcon from "@mui/icons-material/DescriptionRounded";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";

function BillUploadSection({
  uploadedFile,
  isAnalyzing,
  analysisSuccess,
  analysisError,
  methodChosen,
  onFileDrop,
  onFileSelect,
  onDragOver,
  onRemoveFile,
  onManualEntry,
}) {
  if (methodChosen === "manual") {
    return null;
  }

  return (
    <Paper
      elevation={0}
      sx={{
        p: 4,
        mb: 4,
        borderRadius: 3,
        background: (theme) =>
          theme.palette.mode === "dark"
            ? "linear-gradient(135deg, #1a237e 0%, #283593 100%)"
            : "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
        color: "white",
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 3 }}>
        <AutoAwesomeRoundedIcon sx={{ fontSize: 40 }} />
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 700, mb: 0.5 }}>
            Analisi Automatica Bolletta
          </Typography>
          <Typography variant="body2" sx={{ opacity: 0.9 }}>
            Carica la tua bolletta e compileremo automaticamente i dati per te
          </Typography>
        </Box>
      </Box>

      {!uploadedFile && (
        <Box>
          <Paper
            onDragOver={onDragOver}
            onDrop={onFileDrop}
            sx={{
              p: 4,
              textAlign: "center",
              border: "2px dashed rgba(255,255,255,0.4)",
              borderRadius: 2,
              bgcolor: "rgba(255,255,255,0.1)",
              cursor: "pointer",
              transition: "all 0.3s ease",
              "&:hover": {
                bgcolor: "rgba(255,255,255,0.15)",
                borderColor: "rgba(255,255,255,0.6)",
              },
            }}
            onClick={() => document.getElementById("file-upload-step").click()}
          >
            <CloudUploadRoundedIcon
              sx={{ fontSize: 60, opacity: 0.8, mb: 2 }}
            />
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
              Trascina qui la bolletta o clicca per caricare
            </Typography>
            <Typography variant="body2" sx={{ opacity: 0.8 }}>
              Formati supportati: PDF, JPG, PNG (max 10MB)
            </Typography>
            <input
              id="file-upload-step"
              type="file"
              accept=".pdf,.jpg,.jpeg,.png"
              style={{ display: "none" }}
              onChange={onFileSelect}
            />
          </Paper>

          <Box sx={{ textAlign: "center", my: 3 }}>
            <Divider sx={{ borderColor: "rgba(255,255,255,0.3)" }}>
              <Typography variant="body2" sx={{ px: 2, opacity: 0.9 }}>
                oppure
              </Typography>
            </Divider>
          </Box>

          <Button
            fullWidth
            variant="outlined"
            size="large"
            startIcon={<EditRounded />}
            onClick={onManualEntry}
            sx={{
              borderColor: "rgba(255,255,255,0.4)",
              color: "white",
              py: 1.5,
              fontWeight: 600,
              "&:hover": {
                borderColor: "white",
                bgcolor: "rgba(255,255,255,0.1)",
              },
            }}
          >
            Inserisci i dati manualmente
          </Button>
        </Box>
      )}

      {uploadedFile && (
        <Box>
          <Paper
            elevation={0}
            sx={{
              p: 3,
              bgcolor: "rgba(255,255,255,0.15)",
              borderRadius: 2,
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                <DescriptionRoundedIcon sx={{ fontSize: 40 }} />
                <Box>
                  <Typography variant="body1" sx={{ fontWeight: 600 }}>
                    {uploadedFile.name}
                  </Typography>
                  <Typography variant="caption" sx={{ opacity: 0.8 }}>
                    {(uploadedFile.size / 1024 / 1024).toFixed(2)} MB
                  </Typography>
                </Box>
              </Box>
              <IconButton
                onClick={onRemoveFile}
                sx={{
                  color: "white",
                  "&:hover": { bgcolor: "rgba(255,255,255,0.1)" },
                }}
              >
                <DeleteRoundedIcon />
              </IconButton>
            </Box>

            {isAnalyzing && (
              <Box sx={{ mt: 2 }}>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    mb: 1,
                  }}
                >
                  <CircularProgress size={20} sx={{ color: "white" }} />
                  <Typography variant="body2">Analisi in corso...</Typography>
                </Box>
                <LinearProgress
                  sx={{
                    bgcolor: "rgba(255,255,255,0.2)",
                    "& .MuiLinearProgress-bar": {
                      bgcolor: "white",
                    },
                  }}
                />
              </Box>
            )}

            {analysisSuccess && !isAnalyzing && (
              <Alert
                severity="success"
                icon={<CheckCircleRoundedIcon />}
                sx={{
                  mt: 2,
                  bgcolor: "rgba(76, 175, 80, 0.2)",
                  color: "white",
                }}
              >
                Bolletta analizzata con successo! I dati sono stati compilati
                automaticamente.
              </Alert>
            )}

            {analysisError && !isAnalyzing && (
              <Alert
                severity="error"
                sx={{
                  mt: 2,
                  bgcolor: "rgba(244, 67, 54, 0.2)",
                  color: "white",
                }}
              >
                {analysisError}
              </Alert>
            )}
          </Paper>
        </Box>
      )}
    </Paper>
  );
}

export default BillUploadSection;
